"use client";

import Image from "next/image";
import { useEffect, useRef, useState } from "react";
import type { MouseEvent as ReactMouseEvent } from "react";
import { Modal } from "@/app/components/Modal";
import { googleFlagUrlForLanguage } from "@/app/components/workspace/language";

export type LanguageDropdownOption<TCode extends string = string> = {
  code: TCode;
  disabled?: boolean;
  name: string;
};

type LanguageDropdownProps<TCode extends string> = {
  activeCode: TCode;
  ariaLabel: string;
  currentLabel?: string;
  disabled?: boolean;
  label: string;
  onSelect: (code: TCode) => Promise<void> | void;
  options: readonly LanguageDropdownOption<TCode>[];
  presentation?: "menu" | "dialog";
  stopPropagation?: boolean;
  variant?: "compact" | "field" | "toolbar";
};

export function LanguageDropdown<TCode extends string>({
  activeCode,
  ariaLabel,
  currentLabel,
  disabled = false,
  label,
  onSelect,
  options,
  presentation = "menu",
  stopPropagation = false,
  variant = "compact",
}: LanguageDropdownProps<TCode>) {
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const activeOption = options.find((option) => sameLanguage(option.code, activeCode))
    ?? { code: activeCode, name: activeCode.toUpperCase() };

  useEffect(() => {
    if (!open || presentation === "dialog") return;

    function closeOnPointerDown(event: MouseEvent) {
      if (menuRef.current && event.target instanceof Node && !menuRef.current.contains(event.target)) {
        setOpen(false);
      }
    }

    function closeOnEscape(event: KeyboardEvent) {
      if (event.key === "Escape") setOpen(false);
    }

    document.addEventListener("mousedown", closeOnPointerDown);
    document.addEventListener("keydown", closeOnEscape);

    return () => {
      document.removeEventListener("mousedown", closeOnPointerDown);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [open, presentation]);

  useEffect(() => {
    if (disabled || options.length === 0) setOpen(false);
  }, [disabled, options.length]);

  function toggle(event: ReactMouseEvent<HTMLButtonElement>) {
    if (stopPropagation) event.stopPropagation();
    setOpen((value) => !value);
  }

  function select(event: ReactMouseEvent<HTMLButtonElement>, option: LanguageDropdownOption<TCode>) {
    if (stopPropagation) event.stopPropagation();
    if (disabled || option.disabled) return;

    setOpen(false);
    void onSelect(option.code);
  }

  return (
    <div className="relative" ref={menuRef}>
      <button
        type="button"
        className={triggerClassName(variant, open)}
        aria-expanded={open}
        aria-haspopup={presentation}
        aria-label={ariaLabel}
        title={activeOption.name}
        onClick={toggle}
        disabled={disabled || options.length === 0}
      >
        {variant === "field" ? (
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-border bg-surface shadow-sm">
            <LanguageFlag code={activeOption.code} className="h-6 w-6" />
          </span>
        ) : (
          <LanguageFlag code={activeOption.code} className="h-5 w-5" />
        )}
        {variant === "field" ? (
          <span className="min-w-0 flex-1 truncate text-sm font-semibold text-text" lang={activeOption.code}>
            {activeOption.name}
          </span>
        ) : null}
        <Chevron open={open} variant={variant} />
      </button>

      {presentation === "dialog" ? (
        <Modal open={open} onClose={() => setOpen(false)} title={label} size="sm">
          <div role="radiogroup" aria-label={label}>
            <LanguageDropdownOptions
              activeCode={activeCode}
              currentLabel={currentLabel}
              disabled={disabled}
              onSelect={select}
              options={options}
              optionRole="radio"
            />
          </div>
        </Modal>
      ) : open ? (
        <div
          className={menuClassName(variant)}
          role="menu"
          aria-label={label}
          onClick={(event) => {
            if (stopPropagation) event.stopPropagation();
          }}
        >
          {variant === "field" ? null : (
            <div className="border-b border-border px-4 py-3">
              <p className="text-xs font-semibold uppercase tracking-wider text-text-muted">
                {label}
              </p>
            </div>
          )}
          <div className="p-1.5">
            <LanguageDropdownOptions
              activeCode={activeCode}
              currentLabel={currentLabel}
              disabled={disabled}
              onSelect={select}
              options={options}
            />
          </div>
        </div>
      ) : null}
    </div>
  );
}

export function LanguageDropdownOptions<TCode extends string>({
  activeCode,
  currentLabel,
  disabled,
  onSelect,
  options,
  optionRole = "menuitemradio",
}: {
  activeCode: TCode;
  currentLabel?: string;
  disabled: boolean;
  onSelect: (
    event: ReactMouseEvent<HTMLButtonElement>,
    option: LanguageDropdownOption<TCode>,
  ) => void;
  options: readonly LanguageDropdownOption<TCode>[];
  optionRole?: "menuitemradio" | "radio";
}) {
  return (
    <>
      {options.map((option) => {
        const active = sameLanguage(option.code, activeCode);

        return (
          <button
            key={option.code}
            type="button"
            role={optionRole}
            aria-checked={active}
            className={`flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-start transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${
              active
                ? "bg-primary/15 font-semibold text-primary"
                : "text-text-secondary hover:bg-surface-hover hover:text-text"
            }`}
            onClick={(event) => onSelect(event, option)}
            disabled={disabled || option.disabled}
          >
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-border bg-bg-elevated shadow-sm">
              <LanguageFlag code={option.code} className="h-5 w-5" />
            </span>
            <span className="min-w-0 flex-1">
              <bdi className="block truncate text-sm font-semibold" lang={option.code}>{option.name}</bdi>
            </span>
            {active ? (
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary text-white">
                <svg viewBox="0 0 24 24" aria-hidden="true" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path strokeLinecap="round" strokeLinejoin="round" d="m5 12 4 4L19 6" />
                </svg>
                {currentLabel ? <span className="sr-only">{currentLabel}</span> : null}
              </span>
            ) : null}
          </button>
        );
      })}
    </>
  );
}

export function LanguageFlag({ code, className }: { code: string; className: string }) {
  return (
    <Image
      src={googleFlagUrlForLanguage(code)}
      alt=""
      width={24}
      height={24}
      className={`${className} object-contain`}
      aria-hidden="true"
      unoptimized
    />
  );
}

function sameLanguage(left: string, right: string) {
  return left.trim().toLowerCase() === right.trim().toLowerCase();
}

function triggerClassName(variant: LanguageDropdownProps<string>["variant"], open: boolean) {
  if (variant === "field") {
    return `flex w-full items-center gap-3 rounded-2xl border bg-bg-elevated p-3 text-start transition-all disabled:cursor-not-allowed disabled:opacity-60 ${
      open
        ? "border-primary/50 ring-4 ring-primary/10"
        : "border-border hover:border-border-strong hover:bg-surface-hover"
    }`;
  }

  const height = variant === "toolbar" ? "h-10" : "h-9";
  const width = variant === "toolbar" ? "w-10 px-0 sm:w-auto sm:px-2.5" : "px-2.5";

  return `inline-flex ${height} ${width} items-center justify-center gap-1.5 rounded-xl border bg-bg-elevated text-text-secondary shadow-sm transition-all hover:border-border-strong hover:bg-surface-hover hover:text-text disabled:cursor-not-allowed disabled:opacity-60 ${
    open ? "border-primary/50 ring-4 ring-primary/10" : "border-border"
  }`;
}

function menuClassName(variant: LanguageDropdownProps<string>["variant"]) {
  if (variant === "field") {
    return "mt-2 rounded-2xl border border-border bg-bg-elevated";
  }

  if (variant === "toolbar") {
    return "fixed inset-x-2 top-16 z-50 max-h-[calc(100dvh-4.5rem)] w-auto overflow-y-auto rounded-2xl border border-border bg-surface shadow-dropdown sm:absolute sm:inset-x-auto sm:end-0 sm:top-full sm:mt-2 sm:w-64";
  }

  return "absolute end-0 top-full z-[260] mt-2 max-h-[calc(100dvh-5rem)] w-64 max-w-[calc(100vw-1rem)] overflow-y-auto rounded-2xl border border-border bg-surface shadow-dropdown";
}

function Chevron({
  open,
  variant,
}: {
  open: boolean;
  variant: LanguageDropdownProps<string>["variant"];
}) {
  return (
    <svg
      viewBox="0 0 24 24"
      aria-hidden="true"
      className={`${variant === "field" ? "h-5 w-5" : "h-3.5 w-3.5"} ${variant === "toolbar" ? "hidden sm:block" : "block"} shrink-0 text-text-muted transition-transform ${open ? "rotate-180" : ""}`}
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="m6 9 6 6 6-6" />
    </svg>
  );
}
